import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import {
  CustomValidators,
  FormModelControl,
  SelectOption,
  today,
  toIso8601String,
} from '@bcl/design-system';
import { of, Subject, takeUntil } from 'rxjs';
import {
  ReplaceMonitorDialogData,
  ReplaceMonitorDialogForm,
  ReplaceMonitorDialogResponse,
  ReplaceMonitorDialogProduct,
} from './replace-monitor-dialog.model';
import { AddProductService } from '../consumables-dialog/consumables-dialog.service';
import { ScannerService } from 'src/app/services/scanner.service';

@Component({
  selector: 'app-replace-monitor-dialog',
  templateUrl: './replace-monitor-dialog.component.html',
  styleUrls: ['./replace-monitor-dialog.component.scss'],
})
export class ReplaceMonitorDialogComponent implements OnInit, OnDestroy {
  public readonly form: FormGroup<FormModelControl<ReplaceMonitorDialogForm>>;

  public partOptions: SelectOption<number>[] = [];
  public instrumentOptions: SelectOption<number>[] = [];
  public productOptions: SelectOption[] = [];
  public warehouseOptions: SelectOption<number>[] = [];
  public selectedProduct: any = null;
  public maxAmount = 0;
  public productsList: any[] = [];
  public scannerActive = false;
  public showBaitNumber = false;
  private scannerTargetControlName: 'serialNumber' | 'baitNumber' =
    'serialNumber';
  private readonly destroyed$ = new Subject<void>();
  private allProducts: any[] = [];

  constructor(
    fb: FormBuilder,
    private dialogRef: MatDialogRef<ReplaceMonitorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReplaceMonitorDialogData,
    private readonly addProductService: AddProductService,
    private readonly dialog: MatDialog,
    private readonly scannerService: ScannerService
  ) {
    dialogRef.updateSize('600px');
    dialogRef.disableClose = true;

    this.form = fb.group<FormModelControl<ReplaceMonitorDialogForm>>({
      instrumentId: fb.control<number | null>(null, Validators.required),
      contractPartId: fb.control<number | null>(null, Validators.required),
      serialNumber: fb.control<string>(''),
      baitNumber: fb.control<string | null>(null),
      outside: fb.control<boolean>(false),
      temporary: fb.control<boolean>(false),
      warehouseId: fb.control<number | null>(null),
      productId: fb.control<number | null>(null),
      amount: fb.control<number | null>(null),
      supplierId: fb.control<number | null>(null),
      monitorName: fb.control<string | null>(null, [Validators.maxLength(5)]),
      productsList: fb.control<ReplaceMonitorDialogProduct[]>([]),
      allowToDisappear: fb.control<boolean>(false),
    }) as FormGroup<FormModelControl<ReplaceMonitorDialogForm>>;

    this.partOptions = data.partOptions.map((x) => ({
      label: x.label,
      value: x.contractPartId,
    }));

    this.warehouseOptions = data.warehouses.map((x) => ({
      label: x.label,
      value: Number(x.value),
    }));

    this.form.get('contractPartId')?.disable();

    this.form
      .get('contractPartId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.form.get('instrumentId')?.reset(null);

        const part = this.data.partOptions.find(
          (y) => y.contractPartId === value
        );
        if (part) {
          const filteredInstruments = this.data.instrumentOptions.filter(
            (x) => x.moduleId === part.moduleId
          );
          this.instrumentOptions = filteredInstruments.map((x) => ({
            label: x.label,
            value: x.value,
          }));
        } else {
          this.instrumentOptions = this.data.instrumentOptions.map((x) => ({
            label: x.label,
            value: x.value,
          }));
        }
      });

    this.form
      .get('instrumentId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        const selected = this.data.instrumentOptions.find(
          (opt) => opt.instrumentId === value
        );
        this.showBaitNumber = selected?.token === 'BAIT';
        if (!this.showBaitNumber) {
          this.form.get('baitNumber')?.setValue(null);
        }
      });

    this.scannerService
      .getScanResultObservable('replace-monitor-dialog')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result && this.scannerTargetControlName) {
          this.form.get(this.scannerTargetControlName)?.setValue(result);
          this.scannerActive = false;
        }
      });
  }

  public get canAllowToDisappear() {
    return this.selectedProduct?.allowToDisappear === true;
  }

  ngOnInit(): void {
    this.instrumentOptions = this.data.instrumentOptions.map((x) => ({
      label: x.label,
      value: x.instrumentId,
    }));

    this.showBaitNumber = this.instrumentOptions.some((option) =>
      option.label?.toLowerCase().includes('bait')
    );

    if (this.partOptions.length > 0) {
      const contractPartToSelect =
        this.data.selectedContractPartId !== null &&
        this.data.selectedContractPartId !== undefined
          ? this.data.selectedContractPartId
          : this.partOptions[0].value;
      this.form.get('contractPartId')?.setValue(contractPartToSelect);
    }

    this.form
      .get('warehouseId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.form.get('productId')?.setValue(null);
        this.productOptions = [];
        this.getProductsForWarehouse(this.form.get('warehouseId')?.value);
      });

    this.form
      .get('productId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((productId) => {
        if (productId !== null && productId !== undefined) {
          this.selectedProduct = this.allProducts.find(
            (p) => p.rowId === productId
          );
          this.updateMaxAmount();
          this.form.get('amount')?.enable();
          this.form
            .get('amount')
            ?.setValidators([Validators.required, Validators.min(1)]);
          this.form.get('amount')?.updateValueAndValidity();
        } else {
          this.selectedProduct = null;
          this.maxAmount = 0;
          this.form.get('amount')?.disable();
          this.form.get('amount')?.setValue(null);
          this.form.get('amount')?.clearValidators();
          this.form.get('amount')?.updateValueAndValidity();
        }
        this.form.get('allowToDisappear')?.setValue(false);
      });

    this.form
      .get('amount')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((amount) => {
        if (amount && amount > this.maxAmount) {
          this.form.get('amount')?.setValue(this.maxAmount);
        }
      });

    if (this.data.defaultCar) {
      const defaultWarehouseExists = this.warehouseOptions.some(
        (option) => option.value === this.data.defaultCar
      );
      if (defaultWarehouseExists) {
        this.form.get('warehouseId')?.setValue(this.data.defaultCar);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public addProductToList(): void {
    const productId = this.form.get('productId')?.value;
    const amount = this.form.get('amount')?.value;
    const warehouseId = this.form.get('warehouseId')?.value;
    const allowToDisappear = this.form.get('allowToDisappear')?.value || false;

    if (
      productId !== null &&
      productId !== undefined &&
      amount &&
      warehouseId
    ) {
      const product = this.allProducts.find((p) => p.rowId === productId);
      if (product) {
        const existingProductIndex = this.productsList.findIndex(
          (p) =>
            p.productId === productId && p.supplierId === product.supplierId
        );

        if (existingProductIndex >= 0) {
          this.productsList[existingProductIndex].amount += amount;
        } else {
          this.productsList.push({
            productId: product.productId,
            name: product.name,
            amount,
            supplier: product.supplier,
            supplierId: product.supplierId,
            allowToDisappear,
          });
        }

        this.form.get('productsList')?.setValue([...this.productsList]);
        this.form.get('amount')?.setValue(null);
        this.form.get('productId')?.setValue(null);
        this.form.get('warehouseId')?.setValue(null);
        this.productOptions = [];
        this.allProducts = [];
      }
    }
  }

  public removeProductFromList(productId: number, supplierId: number): void {
    this.productsList = this.productsList.filter(
      (p) => !(p.productId === productId && p.supplierId === supplierId)
    );
    this.form.get('productsList')?.setValue([...this.productsList]);
  }

  public openScanner(
    target: 'serialNumber' | 'baitNumber' = 'serialNumber'
  ): void {
    this.scannerTargetControlName = target;
    this.scannerActive = true;
  }

  public closeScanner(): void {
    this.scannerActive = false;
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSubmit(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();

      const result: ReplaceMonitorDialogResponse = {
        ...formValue,
        areaId: this.data.areaId,
        oldMonitorId: this.data.oldMonitorId,
        statusId: '1',
        startDate: toIso8601String(today()),
        externalId: undefined,
        initialDate: new Date(),
      };

      this.dialogRef.close(result);
    }
  }

  private getProductsForWarehouse(warehouseId: number | null): void {
    if (warehouseId) {
      this.addProductService
        .getProductsForWarehouse(warehouseId, this.data.moduleId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((products: any) => {
          this.allProducts = products?.elements ?? [];
          let counter = 0;
          this.allProducts.forEach((product) => {
            product.rowId = counter++;
          });
          this.productOptions = this.allProducts.map((product: any) => ({
            value: product.rowId,
            label:
              product.name +
              (product.supplier ? ' | ' + product.supplier : '') +
              ' | ' +
              product.amount,
          }));
        });
    }
  }

  private updateMaxAmount(): void {
    if (this.selectedProduct) {
      this.maxAmount = this.selectedProduct.amount || 0;
    } else {
      this.maxAmount = 0;
    }
  }
}
